import type { DefaultError, InfiniteData, QueryKey } from '@tanstack/react-query'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

type AnnotationItem = {
  tool: 'bounding_box' | 'dot' | 'polygon' | 'line' | 'crosshair'
  label: string
  vertices: {
    x: number
    y: number
  }[]
  annotationId: string
  color?: string
  mediaId: string
}

type TasksReview = {
  isCorrect: boolean | null
  isLocked: boolean
  isRead: boolean
  taskId: string
  taskReviewId: string
  media: { location: string; mediaId: string }[]
  totalComments: string
  totalUnreadComments: string
  annotations: AnnotationItem[]
}

type AnnotationRequest = {
  correctClass?: string
  subclass?: string | null
  wrongClass?: string
  classType?: string
  origin: 'prediction' | 'resolution'
}

export type MSTasksReviewMineResponse = {
  total: number
  totalIncorrectUnread: number
  totalTasksWithComments: number
  totalUnreadComments: number | null
  tasksReview: TasksReview[]
}

export type MSTasksReviewMineRequest = {
  limit?: number
  cursor?: number
  programId?: string
  unreadOnly?: boolean
  annotations?: AnnotationRequest[]
  unreadCommentsOnly?: boolean
  withCommentsOnly?: boolean
  isCorrect?: boolean
  startClosedDate?: string
  endClosedDate?: string
}

export const MSTasksReviewMineKey = 'mission-specialist-tasks-review-mine'
export const MSTasksReviewMineInfiniteKey = `${MSTasksReviewMineKey}-infinite`
export const MSProgramInstanceTasksReviewMineKey = 'mission-specialist-program-instance-tasks-review-mine'
export const MSProgramInstanceTasksReviewMineInfiniteKey = `${MSProgramInstanceTasksReviewMineKey}-infinite`

export type MSProgramInstanceTasksReviewMineRequest = Omit<MSTasksReviewMineRequest, 'programId'> & {
  programInstanceId?: string
}

const request = async (
  params: MSTasksReviewMineRequest,
  pageParam: number
): Promise<MSTasksReviewMineResponse> => {
  const { data } = await apiClient.get<MSTasksReviewMineResponse>(`/int/v3/operators/task-review/mine`, {
    params: { ...params, cursor: pageParam },
  })
  return data
}

export function useGetMine(params: MSTasksReviewMineRequest) {
  return useQuery<MSTasksReviewMineResponse>({
    queryKey: [MSTasksReviewMineKey, params],
    queryFn: () => request({ ...params }, params.cursor ?? 0),
    enabled: Boolean(params?.programId),
    refetchOnWindowFocus: true,
    select: (data) =>
      ({
        ...data,
        ids: data?.tasksReview?.map((task) => task.taskId) ?? [],
      }) as MSTasksReviewMineResponse,
  })
}

export function useGetMineWithInfiniteQuery(
  params: MSTasksReviewMineRequest,
  type: 'incorrect' | 'correct' | 'withComments'
) {
  const { cursor, ...otherParams } = params
  const nextPageParam = getNextPageCursor(params.cursor, params.limit)

  const query = useInfiniteQuery<
    MSTasksReviewMineResponse,
    DefaultError,
    InfiniteData<MSTasksReviewMineResponse>,
    QueryKey,
    number
  >({
    queryKey: [MSTasksReviewMineInfiniteKey, otherParams, type],
    queryFn: ({ pageParam = 0 }) => request({ ...params, cursor }, pageParam),
    enabled: Boolean(params?.programId),
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: () => nextPageParam,
  })

  const hasPages = query?.data?.pages && query?.data?.pages.length > 0
  const total = Number(hasPages ? query.data?.pages[0].total || 0 : 0)

  const allItems = query?.data?.pages.flatMap((page) => page.tasksReview) ?? []

  return {
    ...query,
    total,
    items: [...allItems].reverse(),
  }
}

const programInstanceRequest = async (
  params: MSProgramInstanceTasksReviewMineRequest,
  pageParam: number
): Promise<MSTasksReviewMineResponse> => {
  const { programInstanceId, ...requestParams } = params
  const { data } = await apiClient.get<MSTasksReviewMineResponse>(
    `/int/v3/operators/program-instances/${programInstanceId}/task-review/mine`,
    {
      params: { ...requestParams, cursor: pageParam },
    }
  )
  return data
}

export function useGetMineByProgramInstance(params: MSProgramInstanceTasksReviewMineRequest) {
  return useQuery<MSTasksReviewMineResponse>({
    queryKey: [MSProgramInstanceTasksReviewMineKey, params],
    queryFn: () => programInstanceRequest({ ...params }, params.cursor ?? 0),
    enabled: Boolean(params?.programInstanceId),
    refetchOnWindowFocus: true,
    select: (data) =>
      ({
        ...data,
        ids: data?.tasksReview?.map((task) => task.taskId) ?? [],
      }) as MSTasksReviewMineResponse,
  })
}

export function useGetMineByProgramInstanceWithInfiniteQuery(
  params: MSProgramInstanceTasksReviewMineRequest,
  type: 'incorrect' | 'correct' | 'withComments'
) {
  const { cursor, ...otherParams } = params
  const nextPageParam = getNextPageCursor(params.cursor, params.limit)

  const query = useInfiniteQuery<
    MSTasksReviewMineResponse,
    DefaultError,
    InfiniteData<MSTasksReviewMineResponse>,
    QueryKey,
    number
  >({
    queryKey: [MSProgramInstanceTasksReviewMineInfiniteKey, otherParams, type],
    queryFn: ({ pageParam = 0 }) => programInstanceRequest({ ...params, cursor }, pageParam),
    enabled: Boolean(params?.programInstanceId),
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: () => nextPageParam,
  })

  const hasPages = query?.data?.pages && query?.data?.pages.length > 0
  const total = Number(hasPages ? query.data?.pages[0].total || 0 : 0)
  const allItems = query?.data?.pages.flatMap((page) => page.tasksReview) ?? []
  const items = [...allItems].reverse()

  return {
    ...query,
    total,
    items,
  }
}

const getNextPageCursor = (cursor?: number, limit?: number) =>
  cursor !== undefined && limit !== undefined ? cursor + limit : undefined
